import React, { useState, useEffect, useCallback } from 'react';
import {
  UsersIcon,
  ShieldCheckIcon,
  PlusIcon,
  EllipsisHorizontalIcon,
  PencilIcon,
  TrashIcon,
  KeyIcon,
  UserMinusIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import { Card, Button, Badge, Spinner, Modal, Dropdown, SearchInput } from '../../components/ui';
import { sysadminService } from '../../services/sysadmin.service';
import { toast } from 'react-toastify';
import { cn } from '../../utils/helpers';

interface AdminUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
  lastLoginAt?: string;
  company?: { id: string; name: string } | null;
  roles: { id: string; name: string }[];
}

interface AdminRole {
  id: string;
  name: string;
  description: string;
  isSystem: boolean;
  permissions: { id: string; name: string }[];
}

interface PermissionGroup {
  resource: string;
  permissions: { id: string; name: string; description?: string }[];
}

export const UsersRoles: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'users' | 'roles'>('users');
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [allPermissions, setAllPermissions] = useState<PermissionGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Modal state
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [selectedRole, setSelectedRole] = useState<AdminRole | null>(null);

  // User form state
  const [userForm, setUserForm] = useState({ firstName: '', lastName: '', email: '', roleId: '' });

  // Role form state
  const [roleForm, setRoleForm] = useState({ name: '', description: '' });
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<string[]>([]);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await sysadminService.getUsers({ search: searchTerm });
      setUsers(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm]);

  const fetchRoles = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await sysadminService.getRoles();
      setRoles(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      toast.error('Failed to load roles');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchPermissions = useCallback(async () => {
    try {
      const data = await sysadminService.getPermissions();
      const perms = Array.isArray(data) ? data : data?.data || [];
      // Group permissions by resource
      const grouped: Record<string, { id: string; name: string; description?: string }[]> = {};
      perms.forEach((p: any) => {
        const [resource] = p.name.split(':');
        if (!grouped[resource]) grouped[resource] = [];
        grouped[resource].push(p);
      });
      setAllPermissions(
        Object.entries(grouped).map(([resource, permissions]) => ({ resource, permissions }))
      );
    } catch {
      // Permissions optional for display
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'users') fetchUsers();
    else fetchRoles();
  }, [activeTab, fetchUsers, fetchRoles]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // Filter
  const filteredUsers = users.filter(
    (u) =>
      u.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredRoles = roles.filter(
    (r) =>
      r.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // User actions
  const handleAddUser = () => {
    setSelectedUser(null);
    setUserForm({ firstName: '', lastName: '', email: '', roleId: '' });
    setIsUserModalOpen(true);
  };

  const handleEditUser = (user: AdminUser) => {
    setSelectedUser(user);
    setUserForm({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      roleId: user.roles?.[0]?.id || '',
    });
    setIsUserModalOpen(true);
  };

  const handleSaveUser = async () => {
    try {
      if (selectedUser) {
        await sysadminService.updateUser(selectedUser.id, userForm);
        toast.success('User updated');
      } else {
        await sysadminService.createUser(userForm);
        toast.success('User created');
      }
      setIsUserModalOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save user');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await sysadminService.deleteUser(userId);
      toast.success('User deleted');
      fetchUsers();
    } catch {
      toast.error('Failed to delete user');
    }
  };

  const handleToggleStatus = async (user: AdminUser) => {
    try {
      await sysadminService.toggleUserStatus(user.id);
      toast.success(`User ${user.isActive ? 'deactivated' : 'activated'}`);
      fetchUsers();
    } catch {
      toast.error('Failed to update user status');
    }
  };

  const handleResetPassword = async (userId: string) => {
    if (!window.confirm('Send password reset email to this user?')) return;
    try {
      await sysadminService.resetUserPassword(userId);
      toast.success('Password reset email sent');
    } catch {
      toast.error('Failed to send password reset');
    }
  };

  // Role actions
  const handleAddRole = () => {
    setSelectedRole(null);
    setRoleForm({ name: '', description: '' });
    setSelectedPermissionIds([]);
    setIsRoleModalOpen(true);
  };

  const handleEditRole = async (role: AdminRole) => {
    setSelectedRole(role);
    setRoleForm({ name: role.name, description: role.description });
    setSelectedPermissionIds(role.permissions?.map((p) => p.id) || []);
    setIsRoleModalOpen(true);
  };

  const handleSaveRole = async () => {
    try {
      if (selectedRole) {
        await sysadminService.updateRole(selectedRole.id, roleForm);
        await sysadminService.updateRolePermissions(selectedRole.id, selectedPermissionIds);
        toast.success('Role updated');
      } else {
        const newRole = await sysadminService.createRole(roleForm);
        if (newRole?.id) {
          await sysadminService.updateRolePermissions(newRole.id, selectedPermissionIds);
        }
        toast.success('Role created');
      }
      setIsRoleModalOpen(false);
      fetchRoles();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save role');
    }
  };

  const handleDeleteRole = async (roleId: string) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;
    try {
      await sysadminService.deleteRole(roleId);
      toast.success('Role deleted');
      fetchRoles();
    } catch {
      toast.error('Failed to delete role');
    }
  };

  const togglePermission = (permId: string) => {
    setSelectedPermissionIds((prev) =>
      prev.includes(permId) ? prev.filter((id) => id !== permId) : [...prev, permId]
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-display font-semibold text-secondary-900 dark:text-secondary-50">
          Users & Roles
        </h1>
        <p className="text-secondary-500 dark:text-secondary-400">
          Manage user accounts and role-based access control
        </p>
      </div>

      {/* Tabs + Search + Add */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex bg-secondary-100 dark:bg-secondary-800 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('users')}
            className={cn(
              'flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors',
              activeTab === 'users'
                ? 'bg-white dark:bg-secondary-700 text-primary-600 dark:text-primary-400 shadow-sm'
                : 'text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-secondary-200'
            )}
          >
            <UsersIcon className="w-4 h-4 mr-2" />
            Users
          </button>
          <button
            onClick={() => setActiveTab('roles')}
            className={cn(
              'flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors',
              activeTab === 'roles'
                ? 'bg-white dark:bg-secondary-700 text-primary-600 dark:text-primary-400 shadow-sm'
                : 'text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-secondary-200'
            )}
          >
            <ShieldCheckIcon className="w-4 h-4 mr-2" />
            Roles
          </button>
        </div>

        <div className="flex items-center gap-3">
          <SearchInput
            placeholder="Search..."
            value={searchTerm}
            onChange={setSearchTerm}
          />
          <Button
            onClick={activeTab === 'users' ? handleAddUser : handleAddRole}
            size="sm"
          >
            <PlusIcon className="w-4 h-4 mr-1" />
            Add {activeTab === 'users' ? 'User' : 'Role'}
          </Button>
        </div>
      </div>

      {/* Content */}
      <Card>
        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : activeTab === 'users' ? (
            /* Users Table */
            filteredUsers.length === 0 ? (
              <div className="text-center py-12">
                <UsersIcon className="mx-auto h-12 w-12 text-secondary-400" />
                <h3 className="mt-2 text-sm font-medium text-secondary-900 dark:text-secondary-50">No users found</h3>
                <p className="mt-1 text-sm text-secondary-500">
                  {searchTerm ? 'Try adjusting your search.' : 'Get started by creating a new user.'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-secondary-200 dark:border-secondary-700">
                      <th className="text-left py-3 px-4 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">User</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Roles</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Company</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Status</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Last Login</th>
                      <th className="text-right py-3 px-4 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-200 dark:divide-secondary-700">
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-secondary-50 dark:hover:bg-secondary-800/50">
                        <td className="py-3 px-4">
                          <div className="flex items-center space-x-3">
                            <div className="h-8 w-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                              <span className="text-sm font-medium text-primary-700 dark:text-primary-400">
                                {user.firstName?.[0]}{user.lastName?.[0]}
                              </span>
                            </div>
                            <div>
                              <div className="text-sm font-medium text-secondary-900 dark:text-secondary-50">
                                {user.firstName} {user.lastName}
                              </div>
                              <div className="text-xs text-secondary-500 dark:text-secondary-400">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-1">
                            {user.roles?.map((role) => (
                              <Badge key={role.id} variant="primary" size="sm">{role.name}</Badge>
                            ))}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={cn(
                            'text-sm',
                            user.company
                              ? 'text-secondary-900 dark:text-secondary-50 font-medium'
                              : 'text-secondary-400 italic'
                          )}>
                            {user.company?.name || 'Platform Admin'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant={user.isActive ? 'success' : 'danger'} size="sm" dot>
                            {user.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-sm text-secondary-500 dark:text-secondary-400">
                          {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never'}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Dropdown
                            trigger={
                              <button className="p-1 rounded hover:bg-secondary-100 dark:hover:bg-secondary-700">
                                <EllipsisHorizontalIcon className="w-5 h-5 text-secondary-500" />
                              </button>
                            }
                            items={[
                              { label: 'Edit', icon: <PencilIcon className="w-4 h-4" />, onClick: () => handleEditUser(user) },
                              { label: 'Reset Password', icon: <KeyIcon className="w-4 h-4" />, onClick: () => handleResetPassword(user.id) },
                              {
                                label: user.isActive ? 'Deactivate' : 'Activate',
                                icon: user.isActive ? <UserMinusIcon className="w-4 h-4" /> : <UserPlusIcon className="w-4 h-4" />,
                                onClick: () => handleToggleStatus(user),
                              },
                              { label: 'Delete', icon: <TrashIcon className="w-4 h-4" />, onClick: () => handleDeleteUser(user.id), danger: true },
                            ]}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            /* Roles Table */
            filteredRoles.length === 0 ? (
              <div className="text-center py-12">
                <ShieldCheckIcon className="mx-auto h-12 w-12 text-secondary-400" />
                <h3 className="mt-2 text-sm font-medium text-secondary-900 dark:text-secondary-50">No roles found</h3>
                <p className="mt-1 text-sm text-secondary-500">
                  {searchTerm ? 'Try adjusting your search.' : 'Get started by creating a new role.'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-secondary-200 dark:border-secondary-700">
                      <th className="text-left py-3 px-4 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Role</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Permissions</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Type</th>
                      <th className="text-right py-3 px-4 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-200 dark:divide-secondary-700">
                    {filteredRoles.map((role) => (
                      <tr key={role.id} className="hover:bg-secondary-50 dark:hover:bg-secondary-800/50">
                        <td className="py-3 px-4">
                          <div className="flex items-center space-x-3">
                            <div className="h-8 w-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                              <ShieldCheckIcon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-secondary-900 dark:text-secondary-50">{role.name}</div>
                              <div className="text-xs text-secondary-500 dark:text-secondary-400">{role.description}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-secondary-600 dark:text-secondary-400">
                          {role.permissions?.length || 0} permission{(role.permissions?.length || 0) !== 1 ? 's' : ''}
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant={role.isSystem ? 'secondary' : 'primary'} size="sm">
                            {role.isSystem ? 'System' : 'Custom'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Dropdown
                            trigger={
                              <button className="p-1 rounded hover:bg-secondary-100 dark:hover:bg-secondary-700">
                                <EllipsisHorizontalIcon className="w-5 h-5 text-secondary-500" />
                              </button>
                            }
                            items={[
                              { label: 'Edit', icon: <PencilIcon className="w-4 h-4" />, onClick: () => handleEditRole(role) },
                              ...(!role.isSystem
                                ? [{ label: 'Delete', icon: <TrashIcon className="w-4 h-4" />, onClick: () => handleDeleteRole(role.id), danger: true as const }]
                                : []),
                            ]}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      </Card>

      {/* Add/Edit User Modal */}
      <Modal
        isOpen={isUserModalOpen}
        onClose={() => setIsUserModalOpen(false)}
        title={selectedUser ? 'Edit User' : 'Add User'}
        size="md"
      >
        <div className="space-y-4 p-1">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">First Name</label>
              <input
                type="text"
                value={userForm.firstName}
                onChange={(e) => setUserForm({ ...userForm, firstName: e.target.value })}
                className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">Last Name</label>
              <input
                type="text"
                value={userForm.lastName}
                onChange={(e) => setUserForm({ ...userForm, lastName: e.target.value })}
                className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">Email</label>
            <input
              type="email"
              value={userForm.email}
              onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">Role</label>
            <select
              value={userForm.roleId}
              onChange={(e) => setUserForm({ ...userForm, roleId: e.target.value })}
              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Select role...</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>{role.name}</option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
            <Button variant="secondary" onClick={() => setIsUserModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveUser}>{selectedUser ? 'Update' : 'Create'}</Button>
          </div>
        </div>
      </Modal>

      {/* Add/Edit Role Modal */}
      <Modal
        isOpen={isRoleModalOpen}
        onClose={() => setIsRoleModalOpen(false)}
        title={selectedRole ? 'Edit Role' : 'Add Role'}
        size="lg"
      >
        <div className="space-y-4 p-1">
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">Role Name</label>
            <input
              type="text"
              value={roleForm.name}
              onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">Description</label>
            <input
              type="text"
              value={roleForm.description}
              onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Permissions grid */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">Permissions</label>
            <div className="max-h-64 overflow-y-auto border border-secondary-200 dark:border-secondary-700 rounded-lg p-3 space-y-4">
              {allPermissions.map((group) => (
                <div key={group.resource}>
                  <h4 className="text-xs font-semibold uppercase text-secondary-500 dark:text-secondary-400 mb-2">
                    {group.resource}
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {group.permissions.map((perm) => (
                      <label key={perm.id} className="flex items-center text-sm text-secondary-700 dark:text-secondary-300">
                        <input
                          type="checkbox"
                          checked={selectedPermissionIds.includes(perm.id)}
                          onChange={() => togglePermission(perm.id)}
                          className="h-4 w-4 rounded border-secondary-300 dark:border-secondary-600 text-primary-500 focus:ring-primary-500 mr-2"
                        />
                        {perm.name}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              {allPermissions.length === 0 && (
                <p className="text-sm text-secondary-500">No permissions available</p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
            <Button variant="secondary" onClick={() => setIsRoleModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveRole}>{selectedRole ? 'Update' : 'Create'}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UsersRoles;
